import { useEffect } from 'react';
import { ArrowRightIcon, PhoneIcon } from 'lucide-react';
import { business, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
const TITLE = 'Hot Tub Financing in Brooklyn, MI | Pegasus Spas';
const DESCRIPTION = 'Talk with Pegasus Spas in Brooklyn, Michigan about financing and payment options for the hot tub or outdoor-living purchase you have in mind.';
const CANONICAL = 'https://www.pegasus-spas.com/financing.html';
const askQuestions = ['What is the total purchase amount?', 'What rate and term does the lender offer?', 'Are there fees or promotional conditions?', 'When would payments begin?'];
const relatedLinks = [{
  label: 'Explore Hot Tubs',
  href: routes.hotTubs
}, {
  label: 'Available Spas',
  href: routes.availableSpas
}, {
  label: 'Certified Pre-Owned',
  href: routes.preOwned
}, {
  label: 'Contact Us',
  href: routes.contact
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function FinancingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-1213" data-mp="zkwtscndl17j">
      <section aria-labelledby="financing-h1" className="w-full bg-white" data-id="element-1214" data-mp="14luccqcarx9k">
        <div className="mx-auto max-w-container px-5 pb-12 pt-11 sm:px-8 lg:pb-16 lg:pt-14" data-id="element-1215" data-mp="14jqaedxyl8bx">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-1216" data-mp="pqn1euyfo2wt">
            <div className="lg:col-span-7" data-id="element-1217" data-mp="un51vimqdf3c">
              <p className="text-[14px] font-bold uppercase tracking-[0.02em] text-pegasus-blue" data-id="element-1218" data-mp="bj1l04kfou70">
                Payment &amp; Financing
              </p>
              <h1 id="financing-h1" className="mt-3 font-display text-h1-mobile font-semibold text-ink lg:text-[48px] lg:leading-[56px]" data-id="element-1219" data-mp="6qqmn65cdggk">
                Let&rsquo;s Talk About Financing
              </h1>
              <p className="mt-5 max-w-2xl text-body leading-8 text-ink/80 sm:text-body-lg" data-id="element-1220" data-mp="uhmbpviexxk4">
                Pick out the spa or backyard pieces you love first, so you know the real total.
                Then call or stop in, and we&rsquo;ll walk you through the financing and payment
                options we have right now.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-1221" data-mp="fk3k8lecp68z">
                <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1222" data-mp="35b0810sxnx9x">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1223" data-mp="1ksy3p5n4fmm2" />
                  Call or Text {business.phone}
                </a>
                <SiteLink to={routes.contact} className="inline-flex min-h-[48px] items-center whitespace-nowrap rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-medium text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-1224" data-mp="1powlubhuw8e1">
                  Send Us a Message
                </SiteLink>
              </div>

              <p className="mt-5 max-w-2xl border-l-2 border-ink/20 pl-4 text-[15.5px] leading-6 text-ink/70" data-id="element-1225" data-mp="28iaci4ejdsnu">
                Financing is subject to lender approval and the lender&rsquo;s terms.
              </p>
            </div>

            <div className="lg:col-span-5 lg:pt-2" data-id="element-1226" data-mp="j2atzwp12qvj">
              <div className="rounded-card bg-water px-6 py-7 sm:px-8" data-id="element-1227" data-mp="j32of46w2ik8">
                <h2 className="font-display text-[24px] font-semibold leading-8 text-ink" data-id="element-1228" data-mp="29ilgrh4jmq54">
                  Good Questions to Ask the Lender
                </h2>
                <ul className="mt-4 space-y-3" data-id="element-1229" data-mp="2ikcjqe7p8iva">
                  {askQuestions.map(question => <li key={question} className="flex gap-3 text-[16.5px] leading-7 text-ink/85" data-id="element-1230" data-mp="2eirar3yacoqd">
                      <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-pegasus-magenta" aria-hidden="true" data-id="element-1231" data-mp="1qsx1sxh37kbe" />
                      {question}
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section aria-label="Related pages" className="w-full border-t border-ink/10 bg-white" data-id="element-1232" data-mp="1utgunoumo1rq">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-1233" data-mp="173cwvy44ew8n">
          <ul className="flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-1234" data-mp="132j8z4qohhfr">
            {relatedLinks.map(link => <li key={link.label} data-id="element-1235" data-mp="sxkqmwzy0pvl">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1236" data-mp="xqvz12kelepm">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1237" data-mp="1goyg8v6pm7cv" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>
    </main>;
}